import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Tag, 
  Dropdown, 
  Modal,
  Alert,
  Space,
  Tooltip,
  Input,
  Table,
  Drawer,
  Form,
  Select,
  message
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  MoreOutlined,
  ReloadOutlined,
  SearchOutlined,
  MailOutlined
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { PageHeader, useResponsive } from '@municipality/ui';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import { useMunicipalities } from '../hooks/useMunicipalities';
import { Municipality } from '../services/municipalities';
import type { ColumnsType } from 'antd/es/table';
import { Card } from 'antd';

const { Option } = Select;

const MunicipalityCard = styled(Card)`
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  cursor: pointer;
  
  .ant-card-body {
    padding: 20px 12px;
  }
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const CardContent = styled(Space)`
  width: 100%;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const TitleText = styled.div`
  font-size: 16px;
  font-weight: 500;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  min-width: 0;
`;

const ActionsContainer = styled.div`
  flex-shrink: 0;
`;

const ActionsButton = styled(Button)`
  min-width: 32px;
  height: 32px;
  background-color: #f5f5f5;
  transition: background-color 0.2s ease, transform 0.1s ease;
  
  &:hover {
    background-color: #e8e8e8;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const DescriptionText = styled.div`
  font-size: 14px;
  color: #666;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoText = styled.span`
  font-size: 14px;
`;

const SearchButtonContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: 8px;
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
  align-items: center;
`;

const SearchInput = styled(Input)`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const AddButton = styled(Button)`
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

interface InviteFormData {
  municipalityName: string;
  email: string;
  country: string;
  adminFirstName: string;
  adminLastName: string;
  status: 'trial' | 'provisional' | 'active';
}

const Municipalities: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  
  const {
    municipalities,
    loading,
    error,
    createMunicipality,
    deleteMunicipality
  } = useMunicipalities();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, React.Key[] | null>>({});
  const [sortedInfo, setSortedInfo] = useState<{ columnKey?: string; order?: 'ascend' | 'descend' }>({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [municipalityToDelete, setMunicipalityToDelete] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [inviteDrawerVisible, setInviteDrawerVisible] = useState(false);
  const [inviteForm] = Form.useForm();

  const onTableChange = (
    pagination: Parameters<NonNullable<React.ComponentProps<typeof Table<Municipality>>['onChange']>>[0],
    filters: Parameters<NonNullable<React.ComponentProps<typeof Table<Municipality>>['onChange']>>[1],
    sorter: Parameters<NonNullable<React.ComponentProps<typeof Table<Municipality>>['onChange']>>[2]
  ) => {
    setFilteredInfo(filters as Record<string, React.Key[] | null>);
    setSortedInfo(sorter as { columnKey?: string; order?: 'ascend' | 'descend' });
    setCurrentPage(pagination?.current || 1);
  };

  const resetFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentPage(1);
    setSearchText('');
  };

  // Count active filters from all columns
  const activeFilterCount = Object.values(filteredInfo).reduce((count, filter) => {
    if (filter && Array.isArray(filter) && filter.length > 0) {
      return count + filter.length;
    }
    return count;
  }, 0);

  // Normalize strings for Greek-friendly search:
  // - Lowercase
  // - Strip diacritics (τόνος, διαλυτικά)
  // - Normalize final sigma 'ς' to 'σ'
  const normalizeGreekSearch = (text: string): string => {
    if (!text) return '';
    let s = text.toLowerCase();
    // Decompose and remove combining marks (diacritics)
    s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Normalize final sigma to standard sigma
    s = s.replace(/ς/g, 'σ');
    return s;
  };

  const displayedItems = React.useMemo(() => {
    if (!searchText.trim()) return municipalities;
    const needle = normalizeGreekSearch(searchText.trim());
    return municipalities.filter((item) => 
      normalizeGreekSearch(item.name).includes(needle) ||
      normalizeGreekSearch(item.email).includes(needle) ||
      normalizeGreekSearch(item.country).includes(needle)
    );
  }, [municipalities, searchText]);

  // Apply sorting to displayed items
  const sortedAndFilteredItems = React.useMemo(() => {
    let items = [...displayedItems];
    
    // Apply column filters
    if (filteredInfo.status && Array.isArray(filteredInfo.status) && filteredInfo.status.length > 0) {
      items = items.filter(item => filteredInfo.status?.includes(item.status));
    }
    if (filteredInfo.country && Array.isArray(filteredInfo.country) && filteredInfo.country.length > 0) {
      items = items.filter(item => filteredInfo.country?.includes(item.country));
    }
    
    if (sortedInfo.columnKey && sortedInfo.order) {
      items.sort((a, b) => {
        let aValue: string | number | Date;
        let bValue: string | number | Date;
        
        switch (sortedInfo.columnKey) {
          case 'displayId':
            aValue = a.displayId || 0;
            bValue = b.displayId || 0;
            break;
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt);
            bValue = new Date(b.createdAt);
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) {
          return sortedInfo.order === 'ascend' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortedInfo.order === 'ascend' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return items;
  }, [displayedItems, sortedInfo, filteredInfo]);

  const handleDelete = (id: string) => {
    setMunicipalityToDelete(id);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!municipalityToDelete) return;
    
    try {
      await deleteMunicipality(municipalityToDelete);
      setDeleteModalVisible(false);
      setMunicipalityToDelete(null);
      message.success('Municipality deleted successfully');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete municipality';
      message.error(errorMessage);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setMunicipalityToDelete(null);
  };

  const handleInvite = async (values: InviteFormData) => {
    try {
      await createMunicipality({
        name: values.municipalityName,
        email: values.email,
        country: values.country,
        phone: undefined,
        adminFirstName: values.adminFirstName,
        adminLastName: values.adminLastName,
        status: values.status || 'trial', // Default to 'trial' if not specified
      });
      
      message.success('Municipality created and invitation sent successfully!');
      setInviteDrawerVisible(false);
      inviteForm.resetFields();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create municipality';
      message.error(errorMessage);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'provisional': return 'blue';
      case 'trial': return 'orange';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Custom card renderer for Municipalities
  const renderMunicipalityCard = (
    record: Municipality,
    index: number,
    _columns: ColumnsType<Municipality>,
    actionsColumn?: ColumnsType<Municipality>[number],
    actionItems?: (record: Municipality) => Array<{
      key: string;
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      danger?: boolean;
    }>
  ) => {
    return (
      <MunicipalityCard
        key={record.id}
        size="small"
      >
        <CardContent direction="vertical" size={12}>
          {/* Title with actions dropdown on the same row */}
          <TitleRow>
            <TitleText>
              {record.name}
            </TitleText>
            {/* Actions dropdown */}
            {actionItems ? (
              <ActionsContainer>
                <Dropdown
                  menu={{
                    items: actionItems(record).map(item => ({
                      key: item.key,
                      label: item.label,
                      icon: item.icon,
                      onClick: item.onClick,
                      danger: item.danger,
                    })),
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                  overlayStyle={{ zIndex: 1050 }}
                >
                  <ActionsButton 
                    type="text" 
                    icon={<MoreOutlined />} 
                    size="small"
                  />
                </Dropdown>
              </ActionsContainer>
            ) : actionsColumn && actionsColumn.render ? (
              <ActionsContainer>
                {actionsColumn.render(undefined, record, index) as React.ReactNode}
              </ActionsContainer>
            ) : null}
          </TitleRow>
          
          {/* Email */}
          {record.email && (
            <DescriptionText>
              {record.email}
            </DescriptionText>
          )}
          
          {/* Country and Status on same row */}
          <InfoRow>
            {/* Country */}
            <InfoContainer>
              <InfoText>{record.country}</InfoText>
            </InfoContainer>
            
            {/* Status */}
            <div>
              <Tag color={getStatusColor(record.status)}>
                {formatStatus(record.status)}
              </Tag>
            </div>
          </InfoRow>
        </CardContent>
      </MunicipalityCard>
    );
  };

  const columns: ColumnsType<Municipality> = [
    {
      title: 'ID',
      dataIndex: 'displayId',
      key: 'displayId',
      width: 80,
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'displayId' ? sortedInfo.order : null,
      render: (displayId: number | undefined) => displayId || '-'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      render: (text: string, record: Municipality) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 120,
      render: (country: string) => country,
      filters: Array.from(new Set((displayedItems || []).map((m) => m.country))).map((country) => ({
        text: country,
        value: country,
      })),
      filteredValue: filteredInfo.country || null,
      onFilter: (value: React.Key | boolean, record: Municipality) => record.country === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {formatStatus(status)}
        </Tag>
      ),
      filters: [
        { text: 'Trial', value: 'trial' },
        { text: 'Provisional', value: 'provisional' },
        { text: 'Active', value: 'active' }
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value: React.Key | boolean, record: Municipality) => record.status === value,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => {
        if (!date) return '-';
        const dateObj = new Date(date);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      },
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: Municipality) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: 'View details',
                icon: <EyeOutlined />,
                onClick: () => navigate(`/municipalities/${record.id}`),
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />,
                onClick: () => console.log('Edit municipality:', record.id),
              },
              {
                key: 'invite',
                label: 'Send invitation',
                icon: <MailOutlined />,
                onClick: () => {
                  inviteForm.setFieldsValue({
                    municipalityName: record.name,
                    email: record.email
                  });
                  setInviteDrawerVisible(true);
                },
              },
              {
                type: 'divider' as const,
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <DeleteOutlined />,
                onClick: () => handleDelete(record.id),
                danger: true,
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Municipalities"
        extra={
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
            <SearchButtonContainer isMobile={isMobile}>
              <SearchInput
                placeholder="Search municipalities..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                suffix={<SearchOutlined />}
              />
              <AddButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setInviteDrawerVisible(true)}
              />
            </SearchButtonContainer>
            {/* Reset filters button - Only show on desktop when filters are active */}
            {!isMobile && activeFilterCount > 0 && (
              <Tooltip title="Reset filters">
                <Button aria-label="Reset filters" icon={<ReloadOutlined />} onClick={resetFilters} />
              </Tooltip>
            )}
          </Space>
        }
      />

      {/* Debug Information */}
      {error && (
        <Alert
          message="Error Loading Municipalities"
          description={`${error}`}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <ResponsiveDataTable<Municipality>
        dataSource={sortedAndFilteredItems}
        columns={columns}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          total: sortedAndFilteredItems.length,
          onChange: setCurrentPage,
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        rowKey="id"
        onChange={onTableChange}
        maxMobileColumns={3}
        showMobileActions={true}
        mobileActionItems={(record) => [
          {
            key: 'view',
            label: 'View details',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/municipalities/${record.id}`),
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            onClick: () => console.log('Edit municipality:', record.id),
          },
          {
            key: 'invite',
            label: 'Send invitation',
            icon: <MailOutlined />,
            onClick: () => {
              inviteForm.setFieldsValue({
                municipalityName: record.name,
                email: record.email
              });
              setInviteDrawerVisible(true);
            },
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            onClick: () => handleDelete(record.id),
            danger: true,
          },
        ]}
        customCardRenderer={renderMunicipalityCard}
      />

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
      >
        <p>Are you sure you want to delete this municipality? This action cannot be undone.</p>
      </Modal>

      <Drawer
        title="Invite municipality"
        open={inviteDrawerVisible}
        onClose={() => {
          setInviteDrawerVisible(false);
          inviteForm.resetFields();
        }}
        width={600}
        placement="right"
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInvite}
        >
          <Form.Item
            name="municipalityName"
            label="Municipality name"
            rules={[{ required: true, message: 'Please enter municipality name' }]}
          >
            <Input placeholder="Enter municipality name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email address"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="adminFirstName"
            label="Admin first name"
            rules={[{ required: true, message: 'Please enter admin first name' }]}
          >
            <Input placeholder="Enter admin first name" />
          </Form.Item>

          <Form.Item
            name="adminLastName"
            label="Admin last name"
            rules={[{ required: true, message: 'Please enter admin last name' }]}
          >
            <Input placeholder="Enter admin last name" />
          </Form.Item>

          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please select country' }]}
          >
            <Select placeholder="Select country">
              <Option value="Greece">Greece</Option>
              <Option value="Cyprus">Cyprus</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
            initialValue="trial"
            tooltip="Trial: Testing phase (hidden from citizens). Provisional: Setup phase (hidden from citizens). Active: Fully operational (visible to citizens)."
          >
            <Select placeholder="Select status">
              <Option value="trial">
                <Tag color="orange">Trial</Tag>
                <span style={{ marginLeft: 8 }}>Municipality is testing the system</span>
              </Option>
              <Option value="provisional">
                <Tag color="blue">Provisional</Tag>
                <span style={{ marginLeft: 8 }}>Municipality will use the system (setup phase)</span>
              </Option>
              <Option value="active">
                <Tag color="green">Active</Tag>
                <span style={{ marginLeft: 8 }}>Municipality is fully operational</span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Send invitation
              </Button>
              <Button onClick={() => {
                setInviteDrawerVisible(false);
                inviteForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default Municipalities;
